import {
  ChangeEventHandler,
  KeyboardEventHandler,
  forwardRef,
  useEffect,
  useRef,
  useState,
  useDeferredValue,
  ChangeEvent,
  ReactElement,
  useMemo,
} from 'react'
import { Listbox, Portal, Transition } from '@headlessui/react'
import { CheckIcon, SelectorIcon, XIcon } from '@heroicons/react/solid'
import classNames from 'classnames'
import { ChevronDownIcon } from '@heroicons/react/outline'
import { Spinner } from '../Spinner'
import { createEvent } from 'utilities/createEvent'
import { usePopper } from 'react-popper'
import { ConditionalWrapper } from '../ConditionalWrapper'
import { useTranslation } from 'react-i18next'
import { Label } from './Label'
import { render } from 'utilities/Function'

type Options = number | boolean | string | null | undefined

interface BaseSelectOption<T = Options> {
  description?: string | ReactElement
  disabled?: boolean
  value: T
}

interface SimpleSelectOption<T = Options> extends BaseSelectOption<T> {
  altText?: string
  name: string | number
}

interface DynamicSelectOption<T = Options> extends BaseSelectOption<T> {
  altText: string // Required if name is not a string so that searching works.
  name: ReactElement
}

export type SelectOption<T = Options> = SimpleSelectOption<T> | DynamicSelectOption<T>

interface BaseSelectProps<V = Options> {
  allowClear?: boolean
  baseKey?: string
  disabled?: boolean
  filterEnabled?: boolean
  filterValue?: string
  hasMore?: boolean
  hideLabel?: boolean
  isLoadingMore?: boolean
  label?: string | ReactElement
  loadMore?(): void
  name?: string
  onFilterChange?(value: string): void
  options: SelectOption<V>[]
  placeholder?: string | ReactElement
  usePortal?: boolean
}

export interface SingleSelectProps<V = Options> extends BaseSelectProps<V> {
  selected: SelectOption<V> | undefined | null
  onChange(value: SelectOption<V> | null): void
  multiple?: false
}

export interface MultiSelectProps<V = Options> extends BaseSelectProps<V> {
  selected: SelectOption<V>[]
  onChange(value: SelectOption<V>[]): void
  multiple: true
}

export type SelectProps<V = Options> = SingleSelectProps<V> | MultiSelectProps<V>

export const Select = forwardRef<unknown, SelectProps>((props, ref) => {
  const {
    name,
    options: initialOptions = [],
    selected,
    onChange,
    hideLabel = false,
    label = null,
    disabled = false,
    filterEnabled = true,
    loadMore,
    isLoadingMore = false,
    hasMore = false,
    multiple = false,
    baseKey = null,
    usePortal = true,
    filterValue,
    onFilterChange,
    allowClear = false,
  } = props

  const { t } = useTranslation()
  const allowFiltering = Boolean(filterEnabled && (!loadMore || onFilterChange))
  const popperElRef = useRef<HTMLDivElement>(null)
  const [filterRef, setFilterRef] = useState<HTMLInputElement | null>(null)
  const [targetElement, setTargetElement] = useState<HTMLDivElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const isParentResponsibleForFiltering = !!props.onFilterChange
  const [_filterValue, setFilterValue] = useState('')
  const deferredQuery = useDeferredValue(filterValue ?? _filterValue)
  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: 'bottom',
  })

  const handleFilterKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    // Stop propagation of the event if the user is typing in the filter.
    // This stops the default behaviour of filtering by the first character.
    // But still allow the user to use the arrow keys to navigate the list,
    // pressing Enter to select an option and pressing Escape to close the list.
    const allowedKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape']

    if (allowedKeys.includes(e.key)) return

    e.stopPropagation()
  }

  const handleFilterChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    // If the parent is responsible for filtering, we'll just call the
    // parent's onFilterChange handler and let it handle the filtering.
    if (isParentResponsibleForFiltering) {
      onFilterChange?.(e.currentTarget.value)
      return
    }

    // Otherwise, we'll update our local state of the value and filter
    // the options ourselves.
    setFilterValue(e.currentTarget.value)
  }

  // When the list is expanded, focus the filter input (if it's there).
  useEffect(() => {
    if (filterRef) {
      // Focus the input
      filterRef.focus()
      return
    }

    // The filter input isn't there, so clear the value or tell the
    // parent to clear it.
    if (isParentResponsibleForFiltering) {
      onFilterChange?.('')
    } else {
      setFilterValue('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRef, isParentResponsibleForFiltering])

  const options = useMemo(
    () =>
      !allowFiltering || !deferredQuery
        ? initialOptions
        : initialOptions.filter((option) => {
            const nameToUse = option.altText ?? option.name.toString()
            return nameToUse.toLowerCase().includes(deferredQuery.toLowerCase())
          }),
    [allowFiltering, deferredQuery, initialOptions]
  )

  return (
    <Listbox
      value={multiple ? selected : selected ?? null}
      onChange={onChange}
      disabled={disabled}
      multiple={multiple}
      by="value"
    >
      {({ open }) => (
        <>
          {!hideLabel && label && <Listbox.Label as={Label}>{label}</Listbox.Label>}
          <div className={classNames('relative', { 'mt-1': label && !hideLabel })}>
            <div ref={setTargetElement}>
              <Listbox.Button
                name={name}
                disabled={disabled}
                className="bg-background-input border-border relative w-full border rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-violet focus:border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {render(() => {
                  if (props.multiple || props.selected?.value === undefined) {
                    return (
                      <span className="block truncate text-muted-foreground dark:text-gray-400">
                        {t('select.placeholder')}
                      </span>
                    )
                  }

                  const { selected } = props

                  return (
                    <>
                      <div className="flex items-center">
                        <span className="block truncate">{selected.name}</span>

                        {allowClear && (
                          <button
                            type="button"
                            className="ml-2 text-gray-400 hover:text-gray-600"
                            onClick={(e) => {
                              e.stopPropagation()
                              props.onChange?.(null)
                              props.onFilterChange?.('')
                            }}
                            aria-label="Clear selection"
                          >
                            <XIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {selected.description && (
                        <span className="text-xs block text-muted-foreground">
                          {selected.description}
                        </span>
                      )}
                    </>
                  )
                })}

                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </Listbox.Button>
            </div>

            <ConditionalWrapper
              condition={usePortal}
              wrapper={(children) => <Portal>{children}</Portal>}
            >
              <div
                ref={popperElRef}
                style={{
                  ...styles.popper,
                  width: targetElement?.offsetWidth,
                }}
                className="z-20"
                {...attributes.popper}
              >
                <Transition
                  show={open}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                  beforeEnter={() => setPopperElement(popperElRef.current)}
                  afterLeave={() => setPopperElement(null)}
                >
                  <Listbox.Options
                    static
                    as="div"
                    className="absolute z-20 mt-1 w-full bg-background-input shadow-lg max-h-60 rounded-md ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm"
                  >
                    {allowFiltering && (
                      <div className="z-10 bg-background-input border-b border-border sticky top-0">
                        <input
                          ref={setFilterRef}
                          className="text-sm border-none w-full focus:outline-none focus:ring-0"
                          type="text"
                          placeholder={t('select.search.placeholder')}
                          onKeyDown={handleFilterKeyDown}
                          value={isParentResponsibleForFiltering ? filterValue : _filterValue}
                          onChange={handleFilterChange}
                        />
                      </div>
                    )}

                    <ul className="p-1">
                      {options.map((option) => (
                        <Listbox.Option
                          key={`${baseKey}_${JSON.stringify(option.value)}`}
                          disabled={option.disabled}
                          className={({ active, disabled }) =>
                            classNames(
                              disabled || active ? 'bg-gray-100' : '',
                              disabled ? 'cursor-not-allowed' : '',
                              !active && !disabled ? ' cursor-default' : '',
                              'select-none relative p-2 pl-3 pr-9 rounded-md'
                            )
                          }
                          value={option}
                        >
                          {({ selected }) => (
                            <>
                              <div>
                                <span className="block truncate">{option.name}</span>

                                {option.description && (
                                  <span className="block text-xs text-muted-foreground">
                                    {option.description}
                                  </span>
                                )}
                              </div>

                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </ul>

                    {hasMore && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          loadMore?.()
                        }}
                        disabled={isLoadingMore}
                        className="flex space-x-1 items-center justify-center w-full hover:text-white hover:bg-violet text-gray-700 cursor-default select-none relative p-2 pl-3 pr-9 rounded-md"
                      >
                        {isLoadingMore && (
                          <>
                            <Spinner margin="0" />
                            <span>{t('select.search.loading_results')}</span>
                          </>
                        )}

                        {!isLoadingMore && (
                          <>
                            <ChevronDownIcon className="w-5 h-5" />
                            <span>{t('select.search.load_more')}</span>
                          </>
                        )}
                      </button>
                    )}
                  </Listbox.Options>
                </Transition>
              </div>
            </ConditionalWrapper>
          </div>
        </>
      )}
    </Listbox>
  )
})

interface FormikSelectProps extends Omit<SelectProps, 'onChange'> {
  name: string
  onChange?(event: ChangeEvent<HTMLSelectElement>): void
  value?: string[] | string
}

export const FormikSelect = forwardRef<unknown, FormikSelectProps>(
  ({ onChange: parentOnChange, ...props }, ref) => {
    const handleOnChange: SelectProps['onChange'] = (value) => {
      parentOnChange?.(createEvent({ value: value?.value ?? '', name: props.name }))
    }

    const fallback: SelectProps['selected'] = props.multiple ? [] : undefined

    return (
      // @ts-expect-error
      <Select
        onChange={handleOnChange}
        ref={ref}
        {...props}
        selected={
          props.value !== undefined
            ? props.options.find((option) => option.value === props.value)
            : fallback
        }
      />
    )
  }
)
